// Clean up → Closed confirmation (feedback round three, mockup
// project-page ?view=cleanup, screens cleanup-close-confirm-*). A project
// only closes once its footage has a physical home: a backup drive (prefilled
// when one is already linked as Backup) or "No footage". Freelance kinds also
// tick "Selects exported" (Henry, after the mockups). Modal renders as a
// Sheet on phones, so the desktop page and the phone page share this.

import { useEffect, useMemo, useState } from 'react';
import { DRIVE_KINDS, driveCapacity, type DriveListRow, type ProjectDrive } from '../../shared/drives';
import { SELECTS_REQUIRED_KINDS, type ProjectCloseInput, type ProjectKind, type ProjectPhase, type ProjectRow } from '../../shared/types';
import { api } from '../lib/api';
import { reportError } from '../lib/errorEvents';
import { formatProjectNumber } from '../lib/calendar';
import { Modal, Seg, SelectField } from './primitives';

export interface CloseProjectTarget {
  id: string;
  name: string;
  project_number: number | null;
  project_kind: ProjectKind;
  selects_exported_at?: number | null;
  phase?: ProjectPhase;
}

/** "ARCHIVE-02 · 8 TB HDD" — the mockup's option label. */
function driveOptionLabel(d: DriveListRow): string {
  const kind = DRIVE_KINDS.find((k) => k.value === d.kind)?.label ?? d.kind;
  return `${d.label} · ${d.capacity_gb === null ? kind : `${driveCapacity(d.capacity_gb)} ${kind}`}`;
}

/** Freelance projects owe the client their selects before closing. */
export function needsSelects(p: { project_kind: ProjectKind; selects_exported_at?: number | null }): boolean {
  return SELECTS_REQUIRED_KINDS.includes(p.project_kind) && !p.selects_exported_at;
}

export function CloseProjectModal({ project, onClose, onClosed }: {
  project: CloseProjectTarget;
  onClose: () => void;
  onClosed: (project: ProjectRow) => void;
}) {
  const selectsRequired = SELECTS_REQUIRED_KINDS.includes(project.project_kind);
  const [mode, setMode] = useState<'drive' | 'none'>('drive');
  const [drive, setDrive] = useState<string | null>(null);
  const [linkedBackup, setLinkedBackup] = useState<string | null>(null);
  const [drives, setDrives] = useState<DriveListRow[] | null>(null);
  const [selects, setSelects] = useState(!!project.selects_exported_at);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let live = true;
    Promise.all([
      api<{ drives: ProjectDrive[] }>(`/api/admin/projects/${project.id}/drives`),
      api<{ drives: DriveListRow[] }>('/api/admin/drives'),
    ])
      .then(([linked, all]) => {
        if (!live) return;
        const manual = linked.drives.filter((d) => !d.auto_backup);
        const backup = (manual.find((d) => d.role === 'backup') ?? manual.find((d) => d.role === 'archive'))?.drive_id ?? null;
        setLinkedBackup(backup);
        setDrive((current) => current ?? backup);
        setDrives(all.drives);
      })
      .catch((error) => {
        if (live) { setDrives([]); reportError(error, 'Could not load drives.'); }
      });
    return () => { live = false; };
  }, [project.id]);

  // Physical, live drives only — Backblaze (auto) stays informational.
  const items = useMemo(() => (drives ?? [])
    .filter((d) => !d.auto_backup && (d.status === 'active' || d.id === linkedBackup))
    .map((d) => ({ value: d.id, label: driveOptionLabel(d), sub: d.status === 'active' ? undefined : d.status })),
  [drives, linkedBackup]);

  const backupReady = mode === 'none' || !!drive;
  const selectsReady = !selectsRequired || selects;
  const ready = backupReady && selectsReady && !busy;
  const hint = !backupReady ? 'Choose a drive or mark no footage' : !selectsReady ? 'Tick selects exported' : undefined;

  const submit = async () => {
    if (!ready) return;
    const body: ProjectCloseInput = mode === 'none' ? { no_footage: true } : { backup_drive_id: drive! };
    if (selectsRequired) body.selects_exported = true;
    setBusy(true);
    try {
      const res = await api<{ project: ProjectRow }>(`/api/admin/projects/${project.id}/close`, { method: 'POST', json: body });
      onClosed(res.project);
    } catch (error) {
      reportError(error, 'Could not close the project.');
      setBusy(false);
    }
  };

  const number = project.project_number !== null ? `${formatProjectNumber(project.project_number)} · ` : '';
  return (
    <Modal
      title="Close project"
      context={`${number}${project.name}`}
      width={500}
      mobile="full"
      onClose={onClose}
      hint={hint}
      foot={<>
        <button className="btn" onClick={onClose}>Cancel</button>
        <button className="btn primary" disabled={!ready} onClick={() => void submit()}>{busy ? 'Closing…' : 'Close project'}</button>
      </>}
    >
      <p className="close-copy">{project.phase === 'delivered'
        ? 'A project closes once the footage has a physical home. Confirm where it lives before this project moves to Closed.'
        : 'Clean up is finished once the footage has a physical home. Confirm where it lives before this project moves to Closed.'}</p>
      <div className="form-grid close-form">
        <div className="f-field span-2">
          <label className="f-label">Physical backup</label>
          <Seg options={[{ value: 'drive', label: 'Backup drive' }, { value: 'none', label: 'No footage' }] as const} value={mode} onChange={setMode} />
        </div>
        {mode === 'drive' ? (
          <div className="f-field span-2">
            <label className="f-label">Backup drive</label>
            <SelectField items={items} value={drive} onChange={setDrive} searchable placeholder={drives ? 'Choose drive…' : 'Loading drives…'} label="Backup drive" />
            <p className="close-small">
              {linkedBackup && drive === linkedBackup
                ? 'Already linked to this project as a physical backup.'
                : 'Links this drive as the project’s Backup. Backblaze cloud backup is unchanged.'}
            </p>
          </div>
        ) : (
          <div className="f-field span-2">
            <p className="close-small close-note">Marks the project as having no footage to keep. It won’t appear in Drives → Untagged.</p>
          </div>
        )}
        {selectsRequired && (
          <div className="f-field span-2">
            <label className="f-label">Freelance selects</label>
            <label className="ft-check">
              <input type="checkbox" checked={selects} onChange={(e) => setSelects(e.target.checked)} />
              Selects exported to the client
            </label>
          </div>
        )}
      </div>
    </Modal>
  );
}
